import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Scale, Globe2 } from "lucide-react";
import { Section, GlassCard, Eyebrow, StatBlock } from "../components/Section";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const HeroBlock = () => (
  <section className="relative pt-32 pb-24 overflow-hidden">
    <div className="absolute inset-0 opacity-60"><NeuralNetworkBackground density={26} /></div>
    <ScaleWatermark />
    <div className="turquoise-aura -top-32 -left-32 opacity-50" />
    <div className="grain-overlay" />
    <div className="container relative z-10 mx-auto px-6 max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
        <div className="aj-chip mb-6"><Sparkles className="w-3 h-3" /> About AccessJustice.Global</div>
        <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05]">
          We are building{" "}
          <span className="italic text-aj-gradient">the Operating System for Justice.</span>
        </h1>
        <p className="font-inter text-base md:text-lg text-white/65 mt-6 max-w-3xl leading-relaxed">
          AccessJustice.Global is a Nigerian innovation with global ambition — combining the simplicity of Uber,
          the trust of banking systems, the intelligence of modern AI, and the accountability of justice institutions.
        </p>
      </motion.div>
    </div>
  </section>
);

const About = () => (
  <div className="bg-ink text-white" data-testid="about-page">
    <HeroBlock />

    {/* Mission / Vision */}
    <Section>
      <div className="grid md:grid-cols-2 gap-6">
        <GlassCard glow>
          <Eyebrow className="mb-4">Our Mission</Eyebrow>
          <h2 className="font-playfair text-3xl text-white mb-4 leading-tight">
            Democratize access to justice through AI, infrastructure and verified professionals.
          </h2>
          <p className="font-inter text-base text-white/65 leading-relaxed">
            Over 5 billion people globally lack meaningful access to justice — not because lawyers don&apos;t exist,
            but because legal services are expensive, complex, slow and inaccessible. We exist to change that.
          </p>
        </GlassCard>
        <GlassCard>
          <Eyebrow className="mb-4">Our Vision</Eyebrow>
          <h2 className="font-playfair text-3xl text-white mb-4 leading-tight">
            Become the world&apos;s leading digital justice infrastructure platform.
          </h2>
          <p className="font-inter text-base text-white/65 leading-relaxed">
            We measure success not in users acquired, but in justice gaps closed — beginning in Nigeria,
            expanding across West Africa, and partnering globally for SDG 16.
          </p>
        </GlassCard>
      </div>
    </Section>

    {/* Positioning */}
    <Section eyebrow="Positioning" aurora>
      <div className="max-w-4xl">
        <h2 className="font-playfair text-5xl md:text-6xl text-white leading-[1.05]">
          AccessJustice.Global is{" "}
          <span className="italic text-white/50 line-through decoration-1">not a chatbot.</span>
          <br />
          It is{" "}
          <span className="italic text-aj-gradient">the Operating System for Justice.</span>
        </h2>
        <p className="font-inter text-base md:text-lg text-white/65 mt-8 leading-relaxed">
          The citizen does not need to know which lawyer to call. The lawyer does not need to chase random clients.
          The donor does not need to guess whether justice funding works. AccessJustice connects everyone through
          verified data, AI, accountability and transparent payments.
        </p>
      </div>
    </Section>

    {/* What sets us apart */}
    <Section eyebrow="What Sets Us Apart" title={<>Four problems. <span className="italic text-aj-gradient">One platform.</span></>}>
      <div className="grid md:grid-cols-2 gap-5">
        {[
          { t: "Citizens", d: "Don't know where to turn for legal help. Justice-AI gives them a starting point." },
          { t: "Lawyers", d: "Need verified clients and reliable payment. We deliver both — with reputation that compounds." },
          { t: "Donors", d: "Need transparent justice-impact tracking. The Justice Wallet shows every fund disbursed." },
          { t: "Institutions", d: "Need scalable access-to-justice infrastructure. We provide the operating system." },
        ].map((s, i) => (
          <motion.div key={s.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
            <GlassCard glow className="h-full">
              <h3 className="font-playfair text-2xl text-white mb-3">{s.t}</h3>
              <p className="font-inter text-base text-white/65 leading-relaxed">{s.d}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Numbers */}
    <Section eyebrow="By the Numbers" title={<>The opportunity. <span className="italic text-aj-gradient">The urgency.</span></>}>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
        <StatBlock value="5B+" label="People Without Justice" />
        <StatBlock value="200+" label="Practice Areas Supported" />
        <StatBlock value="24/7" label="AI Availability" />
        <StatBlock value="SDG 16" label="Impact Aligned" />
      </div>
    </Section>

    {/* Origin */}
    <Section className="border-t border-white/8">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <Eyebrow>A Nigerian Innovation for the World</Eyebrow>
          <h2 className="font-playfair text-4xl md:text-5xl text-white mt-4 mb-6 leading-tight">
            Built in Nigeria.{" "}
            <span className="italic text-gold-gradient">Designed for the world.</span>
          </h2>
          <p className="font-inter text-base text-white/65 leading-relaxed mb-6">
            Developed with strategic engagement from the Nigerian Bar Association, the Federal Ministry of Justice,
            the Office of the Deputy Speaker, UNDP and other global stakeholders, AccessJustice.Global is positioned
            to place Nigeria at the forefront of AI-powered justice innovation.
          </p>
          <Link to="/contact" data-testid="about-contact-cta" className="btn-aj-primary">
            Get in touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="md:col-span-5">
          <GlassCard className="text-center">
            <Globe2 className="w-12 h-12 text-turquoise mx-auto mb-5" />
            <h3 className="font-playfair text-2xl text-white mb-2">Global Roadmap</h3>
            <p className="font-inter text-sm text-white/60 leading-relaxed mb-5">
              Phase 1 — Nigeria · Phase 2 — West Africa · Phase 3 — Global. Multi-jurisdiction by design.
            </p>
            <Scale className="w-8 h-8 text-gold-300 mx-auto" />
          </GlassCard>
        </div>
      </div>
    </Section>
  </div>
);

export default About;
